import { Navigate, useParams } from 'react-router-dom';

import {
  getBreadcrumbSchema,
  getCourseSchema,
  getLocalBusinessSchema,
  combineSchemas,
} from '../utils/structuredData';
import { getProgramPage, PROGRAM_REDIRECTS } from '../data/programPages';

import SEO from './SEO';
import Breadcrumb from './Breadcrumb';

const ProgramPage = () => {
  const { slug } = useParams();

  if (PROGRAM_REDIRECTS[slug]) {
    return <Navigate to={`/programs/${PROGRAM_REDIRECTS[slug]}`} replace />;
  }

  const program = getProgramPage(slug);

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Program not found</h1>
          <p className="text-xl text-gray-600 mb-6">
            We couldn't find the program page you were looking for.
          </p>
          <a
            href="/#programs"
            className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            See all our programs
          </a>
        </div>
      </div>
    );
  }

  // heroImage: null opts a page out of the photo hero while keeping ogImage for sharing.
  const heroImage = program.heroImage !== undefined ? program.heroImage : program.ogImage;

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: 'Programs', path: '/#programs' },
    { name: program.title, path: `/programs/${slug}` },
  ];

  const schema = combineSchemas(
    getLocalBusinessSchema(),
    getBreadcrumbSchema(breadcrumbItems),
    getCourseSchema({
      name: program.courseSchema.name,
      description: program.courseSchema.description,
      url: `/programs/${slug}`,
      audience: program.courseSchema.audience,
      image: program.ogImage,
    })
  );

  return (
    <div className="min-h-screen">
      <SEO
        title={program.title}
        description={program.metaDescription}
        canonical={`/programs/${slug}`}
        ogImage={program.ogImage}
        keywords={program.keywords}
        jsonLd={schema}
      />

      <Breadcrumb items={breadcrumbItems} />

      {/* Hero */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight: '320px' }}
      >
        {heroImage ? (
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={program.title}
              className="w-full h-full object-cover"
              fetchpriority="high"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-800"></div>
        )}

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-12 bg-white/40"></div>
            <div className="mx-4 w-2 h-2 bg-white/60 rotate-45"></div>
            <div className="h-px w-12 bg-white/40"></div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            {program.h1}
          </h1>

          {program.subtitle && (
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto font-light">
              {program.subtitle}
            </p>
          )}
        </div>

        {/* Bottom curve transition */}
        <div className="absolute -bottom-px left-0 right-0">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto block"
          >
            <path d="M0 60V30C240 10 480 0 720 0C960 0 1200 10 1440 30V60H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Body */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {program.art && (
            <img
              src={program.art.src}
              alt={program.art.alt}
              className="w-full rounded-2xl shadow-md mb-8 md:mb-10"
              loading="lazy"
            />
          )}
          {program.sections.map((section) => (
            <div key={section.heading} className="mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-snug">
                {section.heading}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                {section.body}
              </p>
            </div>
          ))}

          {program.resources && (
            <div className="rounded-2xl border border-red-100 bg-red-50/60 p-5 md:p-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{program.cta.headline}</h2>
              <p className="text-base text-gray-700 mb-4">{program.cta.body}</p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  href="tel:+13524280086"
                  className="inline-flex justify-center rounded-lg bg-red-600 px-5 py-3 font-semibold text-white hover:bg-red-700"
                >
                  Call (352) 428-0086
                </a>
                <a
                  href={`mailto:${program.resources.email}`}
                  className="inline-flex justify-center rounded-lg border border-red-600 px-5 py-3 font-semibold text-red-700 hover:bg-red-50"
                >
                  {program.resources.email}
                </a>
                <a
                  href={program.resources.brochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex justify-center rounded-lg px-5 py-3 font-semibold text-red-700 underline-offset-4 hover:underline"
                >
                  {program.resources.brochureLabel}
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProgramPage;
